import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DISTRITOS, DISTRITOS_BARRIOS, COLUMNAS_EXPORT, CONSELLS_SECTORIALS, CONSELLS_DISTRICTE, TEMAS, isRealDate } from "@/data/terrassa";
import { exportToExcel, exportToPDF } from "@/lib/exporters";
import ContactForm from "@/components/ContactForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Search,
  Plus,
  Pencil,
  Trash2,
  LogOut,
  Download,
  X,
  FileSpreadsheet,
  FileText,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  RotateCcw,
  DatabaseBackup,
  Upload,
  Filter,
} from "lucide-react";

// Definición de columnas ordenables de la tabla
const TABLE_COLUMNS = [
  { key: "nombre",             label: "Nombre",       type: "text"   },
  { key: "apellidos",          label: "Apellidos",    type: "text"   },
  { key: "dni",                label: "DNI",          type: "text"   },
  { key: "direccion",          label: "Dirección",    type: "text"   },
  { key: "telefono",           label: "Teléfono",     type: "text"   },
  { key: "email",              label: "Email",        type: "text"   },
  { key: "es_apoderado",       label: "Apoderado",    type: "bool"   },
  { key: "es_afiliado",        label: "Afiliado",     type: "bool"   },
  { key: "numero_afiliado",    label: "Nº afil.",     type: "number" },
  { key: "permite_publicidad", label: "Publi.",       type: "bool"   },
  { key: "permite_whatsapp",   label: "WhatsApp",     type: "bool"   },
  { key: "distrito",           label: "Distrito",     type: "text"   },
  { key: "barrio",             label: "Barrio",       type: "text"   },
  { key: "tema",               label: "Tema",         type: "text"   },
];

const SPANISH_COLLATOR = new Intl.Collator("es", {
  sensitivity: "base", // ignora mayúsculas y acentos
  numeric: false,
});

// Estado tri-estado del campo afiliado, en función de fecha_baja + es_afiliado
const getAfiliadoState = (c) => {
  if (isRealDate(c.fecha_baja_afiliado)) return "baja";
  if (c.es_afiliado) return "si";
  return "no";
};
// Estado tri-estado del campo apoderado, en función de fecha_baja_apoderado + es_apoderado
const getApoderadoState = (c) => {
  if (isRealDate(c.fecha_baja_apoderado)) return "baja";
  if (c.es_apoderado) return "si";
  return "no";
};
const AFIL_LABEL = { si: "Sí", no: "No", baja: "Baja" };
const APODERADO_ANIOS = [2019, 2023, 2027];

const TRI = ["all", "yes", "no"];
const TRI_LABELS = { all: "Todos", yes: "Sí", no: "No" };
const HAS_LABELS = { all: "Todos", yes: "Con", no: "Sin" };
const PAGE_SIZE = 10;

const initialFilters = {
  q: "",
  apoderado: "all",
  anio_apoderado: "all",
  afiliado: "all",
  publicidad: "all",
  whatsapp: "all",
  email_presence: "all",
  telefono_presence: "all",
  distrito: "all",
  barrio: "all",
  referente_territorio: "all",
  voluntario: "all",
  consell_sectorial: "all",
  consell_sectorial_name: "all",
  consell_districte: "all",
  consell_districte_name: "all",
  voluntario_file: "all",
  legal_docs: "all",
  tema: "all",
};

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const canEdit = isAdmin || user?.role === "editor"; // crear / editar contactos
  const canDelete = isAdmin; // solo admin puede eliminar
  const [contactos, setContactos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc"); // 'asc' | 'desc'
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [selectedCols, setSelectedCols] = useState(
    COLUMNAS_EXPORT.map((c) => c.key)
  );

  const initialExportFilters = {
    afiliado: "all",
    apoderado: "all",
    anio_apoderado: "all",
    publicidad: "all",
    whatsapp: "all",
    distrito: "all",
    barrio: "all",
    referente_territorio: "all",
    voluntario: "all",
    consell_sectorial: "all",
    consell_sectorial_name: "all",
    consell_districte: "all",
    consell_districte_name: "all",
    voluntario_file: "all",
    legal_docs: "all",
    tema: "all",
  };
  const [exportFilters, setExportFilters] = useState(initialExportFilters);

  const updateExportFilter = (patch) => {
    setExportFilters((prev) => {
      const next = { ...prev, ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, "distrito")) {
        next.barrio = "all";
      }
      return next;
    });
  };

  const resetExportFilters = () => setExportFilters(initialExportFilters);

  const fetchContactos = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contactos");
      setContactos(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar contactos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContactos();
  }, []);

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [filters]);

  const barriosForFilter = useMemo(() => {
    if (filters.distrito === "all") return [];
    return DISTRITOS_BARRIOS[filters.distrito] || [];
  }, [filters.distrito]);

  const filtered = useMemo(() => {
    const rawQ = filters.q.trim().toLowerCase();
    // Para teléfono: quitar espacios, guiones, paréntesis, puntos y prefijo +34 / 0034
    const normalizePhone = (s) => {
      let n = (s || "").replace(/[\s\-().]/g, "");
      if (n.startsWith("+34")) n = n.slice(3);
      else if (n.startsWith("0034")) n = n.slice(4);
      return n;
    };
    const qPhone = normalizePhone(rawQ);
    const matchTri = (value, tri) => {
      if (tri === "all") return true;
      return tri === "yes" ? value === true : value === false;
    };
    return contactos.filter((c) => {
      if (rawQ) {
        // Búsqueda libre en nombre, apellidos, DNI, email, número de afiliado (case-insensitive)
        const textBlob = `${c.nombre || ""} ${c.apellidos || ""} ${c.dni || ""} ${c.email || ""} ${c.numero_afiliado || ""}`.toLowerCase();
        const phoneMatch = qPhone && normalizePhone(c.telefono || "").includes(qPhone);
        if (!textBlob.includes(rawQ) && !phoneMatch) return false;
      }
      if (!matchTri(c.es_apoderado, "all") && false) {
        // legacy no-op; reemplazado por la lógica tri-estado de abajo
      }
      // Filtro de apoderado: 4 opciones (all/yes/no/baja)
      if (filters.apoderado !== "all") {
        const st = getApoderadoState(c);
        if (filters.apoderado === "yes" && st !== "si") return false;
        if (filters.apoderado === "no" && st !== "no") return false;
        if (filters.apoderado === "baja" && st !== "baja") return false;
      }
      // Filtro por año de apoderado
      if (filters.anio_apoderado !== "all") {
        const a = parseInt(filters.anio_apoderado, 10);
        if (!Array.isArray(c.anios_apoderado) || !c.anios_apoderado.includes(a)) return false;
      }
      // Filtro de afiliado: ahora con 4 opciones (all/yes/no/baja)
      if (filters.afiliado !== "all") {
        const state = getAfiliadoState(c);
        if (filters.afiliado === "yes" && state !== "si") return false;
        if (filters.afiliado === "no" && state !== "no") return false;
        if (filters.afiliado === "baja" && state !== "baja") return false;
      }
      if (!matchTri(c.permite_publicidad, filters.publicidad)) return false;
      if (!matchTri(c.permite_whatsapp, filters.whatsapp)) return false;
      // Filtro presencia email (Con/Sin/Todos)
      if (filters.email_presence !== "all") {
        const hasEmail = !!(c.email && c.email.trim());
        if (filters.email_presence === "yes" && !hasEmail) return false;
        if (filters.email_presence === "no" && hasEmail) return false;
      }
      // Filtro presencia teléfono (Con/Sin/Todos)
      if (filters.telefono_presence !== "all") {
        const hasTel = !!(c.telefono && c.telefono.trim());
        if (filters.telefono_presence === "yes" && !hasTel) return false;
        if (filters.telefono_presence === "no" && hasTel) return false;
      }
      if (filters.distrito !== "all" && c.distrito !== filters.distrito) return false;
      if (filters.barrio !== "all" && c.barrio !== filters.barrio) return false;
      // Referente Territorio (Sí/No/Todos)
      if (filters.referente_territorio === "yes" && c.es_referente_territorio !== true) return false;
      if (filters.referente_territorio === "no" && c.es_referente_territorio !== false) return false;
      // Voluntariado (Sí/No/Todos)
      if (filters.voluntario === "yes" && c.es_voluntario !== true) return false;
      if (filters.voluntario === "no" && c.es_voluntario !== false) return false;
      // Consell sectorial (pertenece + nombre concreto)
      if (filters.consell_sectorial === "yes" && c.pertenece_consell_sectorial !== true) return false;
      if (filters.consell_sectorial === "no" && c.pertenece_consell_sectorial !== false) return false;
      if (filters.consell_sectorial_name !== "all") {
        if (!Array.isArray(c.consells_sectorials) || !c.consells_sectorials.includes(filters.consell_sectorial_name)) return false;
      }
      // Consell districte
      if (filters.consell_districte === "yes" && c.pertenece_consell_districte !== true) return false;
      if (filters.consell_districte === "no" && c.pertenece_consell_districte !== false) return false;
      if (filters.consell_districte_name !== "all") {
        if (!Array.isArray(c.consells_districte) || !c.consells_districte.includes(filters.consell_districte_name)) return false;
      }
      // Hoja voluntario file
      if (filters.voluntario_file === "yes" && !c.voluntario_file) return false;
      if (filters.voluntario_file === "no" && c.voluntario_file) return false;
      // Documentos legales
      const hasLegal = Array.isArray(c.documentos_legales) && c.documentos_legales.length > 0;
      if (filters.legal_docs === "yes" && !hasLegal) return false;
      if (filters.legal_docs === "no" && hasLegal) return false;
      // Filtro por Tema concreto
      if (filters.tema !== "all") {
        if (!Array.isArray(c.tema) || !c.tema.includes(filters.tema)) return false;
      }
      return true;
    });
  }, [contactos, filters]);

  // Ordenación: respeta filtros y se aplica sobre toda la lista filtrada
  const sortedFiltered = useMemo(() => {
    if (!sortKey) return filtered;
    const col = TABLE_COLUMNS.find((c) => c.key === sortKey);
    if (!col) return filtered;
    const dir = sortDir === "desc" ? -1 : 1;
    const arr = [...filtered];

    if (col.type === "bool") {
      // Caso especial: Afiliado y Apoderado tienen 3 estados
      if (sortKey === "es_afiliado" || sortKey === "es_apoderado") {
        const order = { si: 0, baja: 1, no: 2 };
        const stateFn = sortKey === "es_afiliado" ? getAfiliadoState : getApoderadoState;
        arr.sort((a, b) => (order[stateFn(a)] - order[stateFn(b)]) * dir);
      } else {
        arr.sort((a, b) => (Number(!!b[sortKey]) - Number(!!a[sortKey])) * dir);
      }
    } else if (col.type === "number") {
      arr.sort((a, b) => {
        const av = a[sortKey];
        const bv = b[sortKey];
        const an = av != null && av !== "" ? parseFloat(av) : NaN;
        const bn = bv != null && bv !== "" ? parseFloat(bv) : NaN;
        const aEmpty = isNaN(an);
        const bEmpty = isNaN(bn);
        // Los vacíos siempre al final, sea ASC o DESC
        if (aEmpty && bEmpty) return 0;
        if (aEmpty) return 1;
        if (bEmpty) return -1;
        return (an - bn) * dir;
      });
    } else {
      arr.sort((a, b) => {
        const av = (a[sortKey] ?? "").toString();
        const bv = (b[sortKey] ?? "").toString();
        // Vacíos al final independientemente de la dirección
        if (!av && !bv) return 0;
        if (!av) return 1;
        if (!bv) return -1;
        return SPANISH_COLLATOR.compare(av, bv) * dir;
      });
    }
    return arr;
  }, [filtered, sortKey, sortDir]);

  // Contactos para EXPORTACIÓN (independiente de los filtros de la tabla)
  const exportBarriosOptions = useMemo(() => {
    if (exportFilters.distrito === "all") return [];
    return DISTRITOS_BARRIOS[exportFilters.distrito] || [];
  }, [exportFilters.distrito]);

  const exportContactos = useMemo(() => {
    return contactos.filter((c) => {
      if (exportFilters.apoderado !== "all") {
        const st = getApoderadoState(c);
        if (exportFilters.apoderado === "yes" && st !== "si") return false;
        if (exportFilters.apoderado === "no" && st !== "no") return false;
        if (exportFilters.apoderado === "baja" && st !== "baja") return false;
      }
      if (exportFilters.anio_apoderado !== "all") {
        const a = parseInt(exportFilters.anio_apoderado, 10);
        if (!Array.isArray(c.anios_apoderado) || !c.anios_apoderado.includes(a)) return false;
      }
      if (exportFilters.afiliado !== "all") {
        const st = getAfiliadoState(c);
        if (exportFilters.afiliado === "yes" && st !== "si") return false;
        if (exportFilters.afiliado === "no" && st !== "no") return false;
        if (exportFilters.afiliado === "baja" && st !== "baja") return false;
      }
      if (exportFilters.publicidad === "yes" && c.permite_publicidad !== true) return false;
      if (exportFilters.publicidad === "no" && c.permite_publicidad !== false) return false;
      if (exportFilters.whatsapp === "yes" && c.permite_whatsapp !== true) return false;
      if (exportFilters.whatsapp === "no" && c.permite_whatsapp !== false) return false;
      if (exportFilters.distrito !== "all" && c.distrito !== exportFilters.distrito) return false;
      if (exportFilters.barrio !== "all" && c.barrio !== exportFilters.barrio) return false;
      if (exportFilters.referente_territorio === "yes" && c.es_referente_territorio !== true) return false;
      if (exportFilters.referente_territorio === "no" && c.es_referente_territorio !== false) return false;
      if (exportFilters.voluntario === "yes" && c.es_voluntario !== true) return false;
      if (exportFilters.voluntario === "no" && c.es_voluntario !== false) return false;
      if (exportFilters.consell_sectorial === "yes" && c.pertenece_consell_sectorial !== true) return false;
      if (exportFilters.consell_sectorial === "no" && c.pertenece_consell_sectorial !== false) return false;
      if (exportFilters.consell_sectorial_name !== "all") {
        if (!Array.isArray(c.consells_sectorials) || !c.consells_sectorials.includes(exportFilters.consell_sectorial_name)) return false;
      }
      if (exportFilters.consell_districte === "yes" && c.pertenece_consell_districte !== true) return false;
      if (exportFilters.consell_districte === "no" && c.pertenece_consell_districte !== false) return false;
      if (exportFilters.consell_districte_name !== "all") {
        if (!Array.isArray(c.consells_districte) || !c.consells_districte.includes(exportFilters.consell_districte_name)) return false;
      }
      if (exportFilters.voluntario_file === "yes" && !c.voluntario_file) return false;
      if (exportFilters.voluntario_file === "no" && c.voluntario_file) return false;
      const hasLegal = Array.isArray(c.documentos_legales) && c.documentos_legales.length > 0;
      if (exportFilters.legal_docs === "yes" && !hasLegal) return false;
      if (exportFilters.legal_docs === "no" && hasLegal) return false;
      if (exportFilters.tema !== "all") {
        if (!Array.isArray(c.tema) || !c.tema.includes(exportFilters.tema)) return false;
      }
      return true;
    });
  }, [contactos, exportFilters]);

  const totalPages = Math.max(1, Math.ceil(sortedFiltered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = sortedFiltered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(1);
  };

  const updateFilter = (patch) => {
    setFilters((prev) => {
      const next = { ...prev, ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, "distrito")) {
        next.barrio = "all";
      }
      return next;
    });
  };

  const clearFilters = () => setFilters(initialFilters);

  // Cuenta cuántos filtros activos hay (distintos del estado inicial)
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.q.trim()) n++;
    if (filters.apoderado !== "all") n++;
    if (filters.anio_apoderado !== "all") n++;
    if (filters.afiliado !== "all") n++;
    if (filters.publicidad !== "all") n++;
    if (filters.whatsapp !== "all") n++;
    if (filters.email_presence !== "all") n++;
    if (filters.telefono_presence !== "all") n++;
    if (filters.distrito !== "all") n++;
    if (filters.barrio !== "all") n++;
    if (filters.referente_territorio !== "all") n++;
    if (filters.voluntario !== "all") n++;
    if (filters.consell_sectorial !== "all") n++;
    if (filters.consell_sectorial_name !== "all") n++;
    if (filters.consell_districte !== "all") n++;
    if (filters.consell_districte_name !== "all") n++;
    if (filters.voluntario_file !== "all") n++;
    if (filters.legal_docs !== "all") n++;
    if (filters.tema !== "all") n++;
    return n;
  }, [filters]);

  const onCreateOrUpdate = async (form) => {
    try {
      if (editing) {
        const { data } = await api.put(`/contactos/${editing.id}`, form);
        setContactos((prev) => prev.map((c) => (c.id === editing.id ? data : c)));
        toast.success("Contacto actualizado");
      } else {
        const { data } = await api.post("/contactos", form);
        setContactos((prev) => [data, ...prev]);
        toast.success("Contacto creado");
      }
      setFormOpen(false);
      setEditing(null);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar";
      throw new Error(msg);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/contactos/${toDelete.id}`);
      setContactos((prev) => prev.filter((c) => c.id !== toDelete.id));
      toast.success("Contacto eliminado");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al eliminar");
    } finally {
      setToDelete(null);
    }
  };

  const handleExportExcel = () => {
    if (selectedCols.length === 0) {
      toast.error("Selecciona al menos una columna");
      return;
    }
    if (exportContactos.length === 0) {
      toast.error("Ningún contacto cumple los filtros de exportación");
      return;
    }
    exportToExcel(exportContactos, selectedCols);
    toast.success(`Excel exportado · ${exportContactos.length} contactos`);
  };

  const handleExportPDF = () => {
    if (selectedCols.length === 0) {
      toast.error("Selecciona al menos una columna");
      return;
    }
    if (exportContactos.length === 0) {
      toast.error("Ningún contacto cumple los filtros de exportación");
      return;
    }
    exportToPDF(exportContactos, selectedCols);
    toast.success(`PDF exportado · ${exportContactos.length} contactos`);
  };

  const toggleCol = (key) => {
    setSelectedCols((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Restore (upload JSON) state
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreData, setRestoreData] = useState(null); // { contactos: [...], exported_at?: string }
  const [restoreError, setRestoreError] = useState("");
  const [restoring, setRestoring] = useState(false);

  const openRestoreDialog = () => {
    setRestoreFile(null);
    setRestoreData(null);
    setRestoreError("");
    setRestoreOpen(true);
  };

  const handleRestoreFileChange = (e) => {
    const file = e.target.files?.[0];
    setRestoreFile(file || null);
    setRestoreData(null);
    setRestoreError("");
    if (!file) return;
    const isZip = /\.zip$/i.test(file.name) || file.type === "application/zip";
    if (isZip) {
      // El backend valida el ZIP; solo mostramos preview mínimo
      setRestoreData({ contactos: [], is_zip: true, total: null, filename: file.name });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        const list = Array.isArray(parsed) ? parsed : parsed?.contactos;
        if (!Array.isArray(list) || list.length === 0) {
          setRestoreError("El fichero no contiene un array de contactos válido.");
          return;
        }
        const sinNombre = list.filter((c) => !c?.nombre || !String(c.nombre).trim()).length;
        setRestoreData({
          contactos: list,
          exported_at: parsed?.exported_at,
          total: list.length,
          warn_sin_nombre: sinNombre,
        });
      } catch {
        setRestoreError("No se pudo leer el JSON. Asegúrate de que es el fichero exportado por esta app.");
      }
    };
    reader.onerror = () => setRestoreError("Error al leer el fichero.");
    reader.readAsText(file);
  };

  const handleRestoreSubmit = async () => {
    if (!restoreFile) return;
    setRestoring(true);
    try {
      // Detectar si es ZIP o JSON por extensión/content
      const isZip = /\.zip$/i.test(restoreFile.name) || restoreFile.type === "application/zip";
      if (isZip) {
        const fd = new FormData();
        fd.append("file", restoreFile);
        const { data } = await api.post("/admin/contactos/restore_zip", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`Copia restaurada · ${data.inserted_contactos} contactos + ${data.restored_files} ficheros`);
      } else {
        // Legacy JSON restore
        if (!restoreData?.contactos?.length) return;
        const { data } = await api.post("/admin/contactos/restore", {
          contactos: restoreData.contactos,
        });
        toast.success(`Copia restaurada · ${data.inserted} contactos (sin ficheros)`);
      }
      await fetchContactos();
      setRestoreOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al restaurar");
    } finally {
      setRestoring(false);
    }
  };
  const handleResetFromSeed = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/contactos/reset");
      toast.success(`Base de datos reimportada · ${data.inserted} contactos (${data.deleted} eliminados)`);
      await fetchContactos();
      setResetConfirmOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al reimportar");
    } finally {
      setResetting(false);
    }
  };

  const handleBackupExport = async () => {
    try {
      const res = await api.get("/admin/backup.zip", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `backup_txt_${ts}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Backup ZIP descargado (contactos + ficheros)");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al exportar");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-slate-900 text-white flex items-center justify-center font-display font-black rounded-sm">
              P
            </div>
            <div>
              <h1 className="font-display text-lg font-black tracking-tight text-slate-950 leading-none">
                Base de datos de contactos
              </h1>
              <p className="text-xs text-slate-500 mt-0.5">
                Gestión institucional · Terrassa
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-sm text-slate-600">
                {user?.username}
              </span>
              <span
                className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm ${
                  isAdmin
                    ? "bg-slate-900 text-white"
                    : user?.role === "editor"
                    ? "bg-amber-600 text-white"
                    : "bg-slate-100 text-slate-600 border border-slate-200"
                }`}
                data-testid="user-role-badge"
              >
                {isAdmin ? "Admin" : user?.role === "editor" ? "Editor" : "Colaborador"}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              className="rounded-sm border-slate-300"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 mr-1.5" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="px-6 py-6 space-y-6 max-w-[1600px] mx-auto">
        {/* Filter Panel */}
        <section className="bg-white border border-slate-200" data-testid="filter-panel">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <p className="text-xs uppercase tracking-wider text-slate-700 font-semibold">
                Filtros
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-slate-600 hover:text-slate-900 rounded-sm h-8"
              data-testid="clear-filters-button"
            >
              <X className="w-3.5 h-3.5 mr-1" />
              Limpiar
            </Button>
          </div>

          <div className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="lg:col-span-2">
              <FilterLabel>Buscar (nombre, apellidos, DNI, teléfono, email, nº afiliado)</FilterLabel>
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  value={filters.q}
                  onChange={(e) => updateFilter({ q: e.target.value })}
                  placeholder="Nombre, apellidos, DNI, teléfono, email o nº afiliado..."
                  className="pl-9 rounded-sm border-slate-300"
                  data-testid="search-input"
                />
              </div>
            </div>

            <div>
              <FilterLabel>Apoderado</FilterLabel>
              <Select value={filters.apoderado} onValueChange={(v) => updateFilter({ apoderado: v })}>
                <SelectTrigger className="rounded-sm border-slate-300" data-testid="filter-apoderado-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="yes">Sí</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="baja">Baja</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <FilterLabel>Año apoderado</FilterLabel>
              <Select value={filters.anio_apoderado} onValueChange={(v) => updateFilter({ anio_apoderado: v })}>
                <SelectTrigger className="rounded-sm border-slate-300" data-testid="filter-anio-apoderado-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {APODERADO_ANIOS.map((a) => (
                    <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <FilterLabel>Afiliado</FilterLabel>
              <Select value={filters.afiliado} onValueChange={(v) => updateFilter({ afiliado: v })}>
                <SelectTrigger className="rounded-sm border-slate-300" data-testid="filter-afiliado-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="yes">Sí</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                  <SelectItem value="baja">Baja</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <TriSelect
              label="Permite publicidad"
              value={filters.publicidad}
              onChange={(v) => updateFilter({ publicidad: v })}
              testid="filter-publicidad-select"
            />
            <TriSelect
              label="Publicidad WhatsApp"
              value={filters.whatsapp}
              onChange={(v) => updateFilter({ whatsapp: v })}
              testid="filter-whatsapp-select"
            />
            <TriSelect
              label="Email"
              value={filters.email_presence}
              onChange={(v) => updateFilter({ email_presence: v })}
              testid="filter-email-presence-select"
              labels={HAS_LABELS}
            />
            <TriSelect
              label="Teléfono"
              value={filters.telefono_presence}
              onChange={(v) => updateFilter({ telefono_presence: v })}
              testid="filter-telefono-presence-select"
              labels={HAS_LABELS}
            />

            <div>
              <FilterLabel>Distrito</FilterLabel>
              <Select
                value={filters.distrito}
                onValueChange={(v) => updateFilter({ distrito: v })}
              >
                <SelectTrigger
                  className="rounded-sm border-slate-300"
                  data-testid="filter-distrito-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {DISTRITOS.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <FilterLabel>Barrio</FilterLabel>
              <Select
                value={filters.barrio}
                onValueChange={(v) => updateFilter({ barrio: v })}
                disabled={filters.distrito === "all"}
              >
                <SelectTrigger
                  className="rounded-sm border-slate-300 disabled:opacity-50"
                  data-testid="filter-barrio-select"
                >
                  <SelectValue placeholder={filters.distrito === "all" ? "Elige distrito" : "Todos"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {barriosForFilter.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <TriSelect
              label="Referente Territorio"
              value={filters.referente_territorio}
              onChange={(v) => updateFilter({ referente_territorio: v })}
              testid="filter-referente-territorio-select"
            />
            <TriSelect
              label="Voluntari 2027"
              value={filters.voluntario}
              onChange={(v) => updateFilter({ voluntario: v })}
              testid="filter-voluntario-select"
            />
            <TriSelect
              label="Consell sectorial"
              value={filters.consell_sectorial}
              onChange={(v) => updateFilter({ consell_sectorial: v })}
              testid="filter-consell-sectorial-select"
            />
            <div>
              <FilterLabel>Consell sectorial concreto</FilterLabel>
              <Select
                value={filters.consell_sectorial_name}
                onValueChange={(v) => updateFilter({ consell_sectorial_name: v })}
              >
                <SelectTrigger className="rounded-sm border-slate-300" data-testid="filter-consell-sectorial-name-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {CONSELLS_SECTORIALS.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <TriSelect
              label="Consell districte"
              value={filters.consell_districte}
              onChange={(v) => updateFilter({ consell_districte: v })}
              testid="filter-consell-districte-select"
            />
            <div>
              <FilterLabel>Districte concreto</FilterLabel>
              <Select
                value={filters.consell_districte_name}
                onValueChange={(v) => updateFilter({ consell_districte_name: v })}
              >
                <SelectTrigger className="rounded-sm border-slate-300" data-testid="filter-consell-districte-name-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {CONSELLS_DISTRICTE.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <TriSelect
              label="Hoja Voluntari 2027"
              value={filters.voluntario_file}
              onChange={(v) => updateFilter({ voluntario_file: v })}
              testid="filter-voluntario-file-select"
              labels={HAS_LABELS}
            />
            <TriSelect
              label="Docs legales"
              value={filters.legal_docs}
              onChange={(v) => updateFilter({ legal_docs: v })}
              testid="filter-legal-docs-select"
              labels={HAS_LABELS}
            />
            <div>
              <FilterLabel>Tema</FilterLabel>
              <Select
                value={filters.tema}
                onValueChange={(v) => updateFilter({ tema: v })}
              >
                <SelectTrigger className="rounded-sm border-slate-300" data-testid="filter-tema-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {TEMAS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        {/* Action bar */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
              Resultados
            </p>
            <p className="font-display text-2xl font-black tracking-tight text-slate-950 mt-0.5">
              <span data-testid="results-count">{filtered.length}</span>
              <span className="text-slate-400 text-base font-normal ml-2">
                de {contactos.length} contactos
              </span>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="ml-3 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200 rounded-sm px-2 py-1 align-middle hover:bg-amber-100 transition-colors"
                  data-testid="active-filters-badge"
                  title="Haz clic para limpiar todos los filtros"
                >
                  <Filter className="w-3 h-3" />
                  {activeFilterCount} {activeFilterCount === 1 ? "filtro activo" : "filtros activos"}
                  <X className="w-3 h-3" />
                </button>
              )}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => { setEditing(null); setFormOpen(true); }}
              className="rounded-sm bg-slate-900 hover:bg-slate-800 h-10 disabled:opacity-40"
              data-testid="add-contact-button"
              disabled={!canEdit}
              title={!canEdit ? "Solo administrador o editor pueden crear contactos" : undefined}
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Nuevo contacto
            </Button>
            {isAdmin && (
              <Button
                variant="outline"
                onClick={handleBackupExport}
                className="rounded-sm border-slate-300 h-10"
                data-testid="backup-button"
                title="Descarga un fichero JSON con todos los contactos actuales (copia de seguridad)"
              >
                <DatabaseBackup className="w-4 h-4 mr-1.5" />
                Exportar copia de seguridad
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outline"
                onClick={openRestoreDialog}
                className="rounded-sm border-slate-300 h-10"
                data-testid="restore-backup-button"
                title="Sube un fichero JSON de copia de seguridad y restaura todos los contactos"
              >
                <Upload className="w-4 h-4 mr-1.5" />
                Restaurar copia de seguridad
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outline"
                onClick={() => setResetConfirmOpen(true)}
                className="rounded-sm border-slate-300 h-10"
                data-testid="reset-db-button"
                title="Borra todos los contactos y vuelve a cargar la base de datos original (2.622 contactos)"
              >
                <RotateCcw className="w-4 h-4 mr-1.5" />
                Reimportar BD
              </Button>
            )}
          </div>
        </div>

        {/* Table */}
        <section className="bg-white border border-slate-200" data-testid="contacts-table-wrapper">
          <div className="overflow-x-auto">
            <Table className="min-w-[1600px]">
              <TableHeader>
                <TableRow className="bg-slate-50 border-b-2 border-slate-200 hover:bg-slate-50">
                  {TABLE_COLUMNS.map((col) => (
                    <SortableTh
                      key={col.key}
                      column={col}
                      activeKey={sortKey}
                      direction={sortDir}
                      onClick={() => handleSort(col.key)}
                    />
                  ))}
                  <TableHead className="text-xs uppercase tracking-wider text-slate-600 font-semibold py-3 text-right pr-5 sticky right-0 bg-slate-50 z-10">
                    Acciones
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={TABLE_COLUMNS.length + 1} className="text-center py-12 text-slate-500">
                      Cargando...
                    </TableCell>
                  </TableRow>
                ) : paged.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={TABLE_COLUMNS.length + 1} className="text-center py-12 text-slate-500" data-testid="no-results">
                      No se encontraron contactos con los filtros actuales.
                    </TableCell>
                  </TableRow>
                ) : (
                  paged.map((c) => (
                    <TableRow
                      key={c.id}
                      className="border-b border-slate-100 hover:bg-slate-50 transition-colors duration-150"
                      data-testid={`contact-row-${c.dni || c.id}`}
                    >
                      <TableCell className="py-3 font-semibold text-slate-900 whitespace-nowrap">{c.nombre || "—"}</TableCell>
                      <TableCell className="text-slate-800 whitespace-nowrap">{c.apellidos || "—"}</TableCell>
                      <TableCell className="font-mono text-xs text-slate-700 whitespace-nowrap">{c.dni || "—"}</TableCell>
                      <TableCell className="text-sm text-slate-700 max-w-[200px] truncate" title={c.direccion}>{c.direccion || "—"}</TableCell>
                      <TableCell className="text-sm text-slate-700 whitespace-nowrap">{c.telefono || "—"}</TableCell>
                      <TableCell className="text-sm text-slate-700 max-w-[220px] truncate" title={c.email}>{c.email || "—"}</TableCell>
                      <TableCell><ApoderadoPill contacto={c} /></TableCell>
                      <TableCell><AfiliadoPill contacto={c} /></TableCell>
                      <TableCell className="font-mono text-xs text-slate-700 whitespace-nowrap">{c.numero_afiliado || "—"}</TableCell>
                      <TableCell><Pill on={c.permite_publicidad} /></TableCell>
                      <TableCell><Pill on={c.permite_whatsapp} /></TableCell>
                      <TableCell className="text-sm text-slate-700 whitespace-nowrap">{c.distrito || "—"}</TableCell>
                      <TableCell className="text-sm text-slate-700 whitespace-nowrap">{c.barrio || "—"}</TableCell>
                      <TableCell className="text-sm text-slate-700 max-w-[180px] truncate" title={Array.isArray(c.tema) ? c.tema.join(", ") : (c.tema || "")}>{Array.isArray(c.tema) ? (c.tema.join(", ") || "—") : (c.tema || "—")}</TableCell>
                      <TableCell className="text-right pr-5 sticky right-0 bg-white hover:bg-slate-50 z-10">
                        {canEdit ? (
                          <div className="inline-flex gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => { setEditing(c); setFormOpen(true); }}
                              className="rounded-sm h-8 w-8 hover:bg-slate-100"
                              data-testid={`edit-button-${c.dni || c.id}`}
                              aria-label="Editar"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {canDelete && (
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => setToDelete(c)}
                                className="rounded-sm h-8 w-8 hover:bg-red-50 text-red-600"
                                data-testid={`delete-button-${c.dni || c.id}`}
                                aria-label="Eliminar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {filtered.length > 0 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 text-sm">
              <p className="text-slate-500">
                Página {currentPage} de {totalPages}
              </p>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="rounded-sm border-slate-300 h-8"
                  data-testid="pagination-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="rounded-sm border-slate-300 h-8"
                  data-testid="pagination-next"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </section>

        {/* Export Panel */}
        <section className="bg-white border border-slate-200" data-testid="export-panel">
          <div className="border-b border-slate-200 px-5 py-3 flex items-center gap-2">
            <Download className="w-4 h-4 text-slate-500" />
            <p className="text-xs uppercase tracking-wider text-slate-700 font-semibold">
              Exportar listado
            </p>
            <span className="text-xs text-slate-500">
              · Se exportarán <span data-testid="export-count-header" className="font-semibold text-slate-900">{exportContactos.length}</span> contactos
            </span>
          </div>
          <div className="p-5 space-y-5">

            {/* SECCIÓN 1 — ¿A QUIÉN exportar? */}
            <div className="border border-amber-300 bg-amber-50/40 rounded-sm" data-testid="export-filters-section">
              <div className="flex items-center justify-between border-b border-amber-200 px-4 py-3">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-800 font-bold">
                    1. ¿A quién exportar? <span className="text-slate-500 font-normal normal-case">(filtros)</span>
                  </p>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Estos filtros deciden qué contactos se incluyen en el fichero. No afectan a la tabla de arriba.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={resetExportFilters}
                  className="text-slate-600 hover:text-slate-900 h-8 rounded-sm"
                  data-testid="export-filters-clear"
                >
                  <X className="w-3.5 h-3.5 mr-1" />
                  Limpiar
                </Button>
              </div>

              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <FilterLabel>Afiliado</FilterLabel>
                  <Select value={exportFilters.afiliado} onValueChange={(v) => updateExportFilter({ afiliado: v })}>
                    <SelectTrigger className="rounded-sm border-slate-300 bg-white" data-testid="export-filter-afiliado">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="yes">Sí</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="baja">Baja</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FilterLabel>Apoderado</FilterLabel>
                  <Select value={exportFilters.apoderado} onValueChange={(v) => updateExportFilter({ apoderado: v })}>
                    <SelectTrigger className="rounded-sm border-slate-300 bg-white" data-testid="export-filter-apoderado">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="yes">Sí</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="baja">Baja</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FilterLabel>Año apoderado</FilterLabel>
                  <Select value={exportFilters.anio_apoderado} onValueChange={(v) => updateExportFilter({ anio_apoderado: v })}>
                    <SelectTrigger className="rounded-sm border-slate-300 bg-white" data-testid="export-filter-anio-apoderado">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {APODERADO_ANIOS.map((a) => (
                        <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FilterLabel>Permite publicidad</FilterLabel>
                  <Select value={exportFilters.publicidad} onValueChange={(v) => updateExportFilter({ publicidad: v })}>
                    <SelectTrigger className="rounded-sm border-slate-300 bg-white" data-testid="export-filter-publicidad">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="yes">Sí</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FilterLabel>Permite WhatsApp</FilterLabel>
                  <Select value={exportFilters.whatsapp} onValueChange={(v) => updateExportFilter({ whatsapp: v })}>
                    <SelectTrigger className="rounded-sm border-slate-300 bg-white" data-testid="export-filter-whatsapp">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="yes">Sí</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FilterLabel>Distrito</FilterLabel>
                  <Select value={exportFilters.distrito} onValueChange={(v) => updateExportFilter({ distrito: v })}>
                    <SelectTrigger className="rounded-sm border-slate-300 bg-white" data-testid="export-filter-distrito">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {DISTRITOS.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FilterLabel>Barrio</FilterLabel>
                  <Select
                    value={exportFilters.barrio}
                    onValueChange={(v) => updateExportFilter({ barrio: v })}
                    disabled={exportFilters.distrito === "all"}
                  >
                    <SelectTrigger
                      className="rounded-sm border-slate-300 bg-white disabled:opacity-50"
                      data-testid="export-filter-barrio"
                    >
                      <SelectValue placeholder={exportFilters.distrito === "all" ? "Elige distrito" : "Todos"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {exportBarriosOptions.map((b) => (
                        <SelectItem key={b} value={b}>{b}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <FilterLabel>Referente Territorio</FilterLabel>
                  <Select value={exportFilters.referente_territorio} onValueChange={(v) => updateExportFilter({ referente_territorio: v })}>
                    <SelectTrigger className="rounded-sm border-slate-300 bg-white" data-testid="export-filter-referente-territorio">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="yes">Sí</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FilterLabel>Voluntari 2027</FilterLabel>
                  <Select value={exportFilters.voluntario} onValueChange={(v) => updateExportFilter({ voluntario: v })}>
                    <SelectTrigger className="rounded-sm border-slate-300 bg-white" data-testid="export-filter-voluntario">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="yes">Sí</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FilterLabel>Consell sectorial</FilterLabel>
                  <Select value={exportFilters.consell_sectorial} onValueChange={(v) => updateExportFilter({ consell_sectorial: v })}>
                    <SelectTrigger className="rounded-sm border-slate-300 bg-white" data-testid="export-filter-consell-sectorial">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="yes">Sí</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FilterLabel>Consell sectorial concreto</FilterLabel>
                  <Select value={exportFilters.consell_sectorial_name} onValueChange={(v) => updateExportFilter({ consell_sectorial_name: v })}>
                    <SelectTrigger className="rounded-sm border-slate-300 bg-white" data-testid="export-filter-consell-sectorial-name">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {CONSELLS_SECTORIALS.map((c) => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FilterLabel>Consell districte</FilterLabel>
                  <Select value={exportFilters.consell_districte} onValueChange={(v) => updateExportFilter({ consell_districte: v })}>
                    <SelectTrigger className="rounded-sm border-slate-300 bg-white" data-testid="export-filter-consell-districte">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="yes">Sí</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FilterLabel>Districte concreto</FilterLabel>
                  <Select value={exportFilters.consell_districte_name} onValueChange={(v) => updateExportFilter({ consell_districte_name: v })}>
                    <SelectTrigger className="rounded-sm border-slate-300 bg-white" data-testid="export-filter-consell-districte-name">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {CONSELLS_DISTRICTE.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FilterLabel>Hoja Voluntari 2027</FilterLabel>
                  <Select value={exportFilters.voluntario_file} onValueChange={(v) => updateExportFilter({ voluntario_file: v })}>
                    <SelectTrigger className="rounded-sm border-slate-300 bg-white" data-testid="export-filter-voluntario-file">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="yes">Con</SelectItem>
                      <SelectItem value="no">Sin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FilterLabel>Docs legales</FilterLabel>
                  <Select value={exportFilters.legal_docs} onValueChange={(v) => updateExportFilter({ legal_docs: v })}>
                    <SelectTrigger className="rounded-sm border-slate-300 bg-white" data-testid="export-filter-legal-docs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="yes">Con</SelectItem>
                      <SelectItem value="no">Sin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FilterLabel>Tema</FilterLabel>
                  <Select value={exportFilters.tema} onValueChange={(v) => updateExportFilter({ tema: v })}>
                    <SelectTrigger className="rounded-sm border-slate-300 bg-white" data-testid="export-filter-tema">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {TEMAS.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="px-4 py-3 border-t border-amber-200 bg-amber-50/60">
                <p className="font-display text-xl font-black tracking-tight text-slate-950">
                  Se exportarán <span data-testid="export-count">{exportContactos.length}</span>
                  <span className="text-slate-500 text-sm font-normal ml-2">
                    de {contactos.length} contactos
                  </span>
                </p>
              </div>
            </div>

            {/* SECCIÓN 2 — ¿QUÉ columnas? */}
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-800 font-bold">
                2. ¿Qué columnas incluir?
              </p>
              <p className="text-xs text-slate-600 mt-0.5 mb-3">
                Estas casillas solo deciden qué columnas aparecen en el fichero. No filtran contactos.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                {COLUMNAS_EXPORT.map((col) => (
                  <label
                    key={col.key}
                    className="flex items-center gap-2 px-3 py-2 border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors duration-150 rounded-sm"
                  >
                    <Checkbox
                      checked={selectedCols.includes(col.key)}
                      onCheckedChange={() => toggleCol(col.key)}
                      className="data-[state=checked]:bg-slate-900 data-[state=checked]:border-slate-900 rounded-sm"
                      data-testid={`export-col-${col.key}`}
                    />
                    <span className="text-sm text-slate-800">{col.label}</span>
                  </label>
                ))}
              </div>
              <div className="mt-2 flex gap-2 text-xs">
                <button
                  type="button"
                  onClick={() => setSelectedCols(COLUMNAS_EXPORT.map((c) => c.key))}
                  className="text-slate-600 hover:text-slate-900 underline underline-offset-2"
                  data-testid="select-all-cols"
                >
                  Seleccionar todas
                </button>
                <span className="text-slate-300">·</span>
                <button
                  type="button"
                  onClick={() => setSelectedCols([])}
                  className="text-slate-600 hover:text-slate-900 underline underline-offset-2"
                  data-testid="deselect-all-cols"
                >
                  Ninguna
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-1">
              <Button
                onClick={handleExportExcel}
                className="rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white h-10"
                data-testid="export-excel-button"
              >
                <FileSpreadsheet className="w-4 h-4 mr-1.5" />
                Exportar a Excel
              </Button>
              <Button
                onClick={handleExportPDF}
                variant="outline"
                className="rounded-sm border-slate-300 h-10"
                data-testid="export-pdf-button"
              >
                <FileText className="w-4 h-4 mr-1.5" />
                Exportar a PDF
              </Button>
            </div>
          </div>
        </section>
      </main>

      <ContactForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditing(null); }}
        onSubmit={onCreateOrUpdate}
        initial={editing}
      />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">
              Eliminar contacto
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Seguro que deseas eliminar a{" "}
              <span className="font-semibold">
                {toDelete?.nombre} {toDelete?.apellidos}
              </span>
              ? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm border-slate-300" data-testid="delete-cancel-button">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="rounded-sm bg-red-600 hover:bg-red-700"
              data-testid="delete-confirm-button"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={restoreOpen} onOpenChange={(o) => { if (!restoring) setRestoreOpen(o); }}>
        <AlertDialogContent className="rounded-sm" data-testid="restore-backup-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">
              Restaurar copia de seguridad
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  Sube un fichero ZIP (con contactos y ficheros adjuntos) o JSON exportado con &quot;Exportar copia de seguridad&quot;.
                  Esto <span className="font-semibold">borrará todos los contactos actuales</span> y los reemplazará por los del fichero.
                </p>
                <div>
                  <input
                    type="file"
                    accept="application/zip,.zip,application/json,.json"
                    onChange={handleRestoreFileChange}
                    className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border file:border-slate-300 file:bg-slate-50 file:text-slate-700 file:font-semibold hover:file:bg-slate-100"
                    data-testid="restore-file-input"
                  />
                </div>
                {restoreError && (
                  <p className="text-red-600" data-testid="restore-error">{restoreError}</p>
                )}
                {restoreData && !restoreError && (
                  <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 space-y-1" data-testid="restore-preview">
                    {restoreData.is_zip ? (
                      <p>
                        Fichero ZIP: <span className="font-mono text-xs">{restoreData.filename}</span>. Se validará y restaurará al confirmar.
                      </p>
                    ) : (
                      <p>
                        <span className="font-semibold">{restoreData.total}</span> contactos detectados en el fichero JSON.
                      </p>
                    )}
                    {restoreData.exported_at && (
                      <p className="text-xs text-slate-500">
                        Exportado: {new Date(restoreData.exported_at).toLocaleString("es-ES")}
                      </p>
                    )}
                    {restoreData.warn_sin_nombre > 0 && (
                      <p className="text-xs text-amber-700">
                        Aviso: {restoreData.warn_sin_nombre} contacto(s) sin nombre serán restaurados igual.
                      </p>
                    )}
                    <p className="text-red-600 text-xs">
                      Cualquier edición posterior se perderá.
                    </p>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="rounded-sm border-slate-300"
              disabled={restoring}
              data-testid="restore-cancel-button"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleRestoreSubmit(); }}
              disabled={restoring || !restoreData || !!restoreError}
              className="rounded-sm bg-slate-900 hover:bg-slate-800 disabled:opacity-40"
              data-testid="restore-confirm-button"
            >
              {restoring ? "Restaurando..." : "Sí, restaurar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">
              Reimportar base de datos
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  Esta acción <span className="font-semibold">borrará todos los contactos actuales</span> y cargará
                  los 2.622 contactos originales del fichero de importación.
                </p>
                <p className="text-red-600">
                  Cualquier edición manual posterior se perderá.
                </p>
                <p>¿Continuar?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm border-slate-300" disabled={resetting} data-testid="reset-cancel-button">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleResetFromSeed(); }}
              disabled={resetting}
              className="rounded-sm bg-slate-900 hover:bg-slate-800"
              data-testid="reset-confirm-button"
            >
              {resetting ? "Reimportando..." : "Sí, reimportar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const FilterLabel = ({ children }) => (
  <Label className="text-xs uppercase tracking-wider text-slate-700 font-semibold mb-1.5 block">
    {children}
  </Label>
);

const Th = ({ children, className = "" }) => (
  <TableHead className={`text-xs uppercase tracking-wider text-slate-600 font-semibold py-3 ${className}`}>
    {children}
  </TableHead>
);

const SortableTh = ({ column, activeKey, direction, onClick }) => {
  const isActive = activeKey === column.key;
  const Icon = isActive ? (direction === "asc" ? ChevronUp : ChevronDown) : ChevronsUpDown;
  return (
    <TableHead
      className="text-xs uppercase tracking-wider text-slate-600 font-semibold py-3 whitespace-nowrap select-none"
    >
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 group transition-colors duration-150 ${
          isActive ? "text-slate-900" : "text-slate-600 hover:text-slate-900"
        }`}
        data-testid={`sort-header-${column.key}`}
        aria-label={`Ordenar por ${column.label}`}
      >
        <span>{column.label}</span>
        <Icon
          className={`w-3.5 h-3.5 transition-opacity duration-150 ${
            isActive ? "opacity-100" : "opacity-40 group-hover:opacity-100"
          }`}
          strokeWidth={2.5}
        />
      </button>
    </TableHead>
  );
};

const Pill = ({ on }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-sm ${
      on
        ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
        : "bg-slate-100 text-slate-500 border border-slate-200"
    }`}
  >
    {on ? "Sí" : "No"}
  </span>
);

const AfiliadoPill = ({ contacto }) => {
  const state = getAfiliadoState(contacto);
  const styles = {
    si:   "bg-emerald-50 text-emerald-800 border-emerald-200",
    no:   "bg-slate-100 text-slate-500 border-slate-200",
    baja: "bg-orange-50 text-orange-800 border-orange-200",
  }[state];
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-sm border ${styles}`}
      data-testid={`afiliado-pill-${state}`}
    >
      {AFIL_LABEL[state]}
    </span>
  );
};

const ApoderadoPill = ({ contacto }) => {
  const state = getApoderadoState(contacto);
  const styles = {
    si:   "bg-emerald-50 text-emerald-800 border-emerald-200",
    no:   "bg-slate-100 text-slate-500 border-slate-200",
    baja: "bg-orange-50 text-orange-800 border-orange-200",
  }[state];
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-sm border ${styles}`}
      data-testid={`apoderado-pill-${state}`}
    >
      {AFIL_LABEL[state]}
    </span>
  );
};

const TriSelect = ({ label, value, onChange, testid, labels = TRI_LABELS }) => (
  <div>
    <FilterLabel>{label}</FilterLabel>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="rounded-sm border-slate-300" data-testid={testid}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {TRI.map((v) => (
          <SelectItem key={v} value={v}>{labels[v]}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);
